import React, { useState, useEffect, useMemo } from 'react'
import { startOfDay, subDays, endOfDay } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz'
import { useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'

import {
  fetchLoyaltyAnalytics,
  selectLoyaltyAnalytics,
} from '../../../../../reducers/loyaltyReducer'
import {
  selectBuildingsForFilter,
  selectActiveBuilding,
} from '../../../../../reducers/buildingSelectorReducer'
import { selectUserRole } from '../../../../../reducers/userDataReducer'
import AnalyticsItem from '../../components/AnalyticsItem/AnalyticsItem'
import Upselling from '../../../../Upselling/Upselling'
import Icon from '../../../../Icons'
import { getLoyaltyAnalyticsMapping } from '../../helpers'

import LoyaltyTable from './LoyaltyTable'

const DEFAULT_DAYS_RANGE = 30
const INITIAL_DATE_FILTER = [
  subDays(startOfDay(Date.now()), DEFAULT_DAYS_RANGE),
  startOfDay(Date.now()),
]

const RewardsLoyalty = ({
  buildings,
  analytics,
  fetchLoyaltyAnalytics,
  buildingData,
  userRole,
}) => {
  const { t } = useTranslation(['rewards-dashboard'])
  const [isLoadingAnalytics, setLoadingAnalytics] = useState(true)
  const [showAnalytics, setShowAnalytics] = useState(true)
  const [dateFilter, setDateFilter] = useState(INITIAL_DATE_FILTER)

  const { buildingId } = useParams()
  const { loyaltyAvailable } = buildingData || {}

  const selectedBuilding = useMemo(
    () => buildings.find((building) => building.id === Number(buildingId)),
    [buildings, buildingId],
  )

  const fetchAnalytics = async () => {
    const [startDate, endDate] = dateFilter
    if (startDate && endDate) {
      setLoadingAnalytics(true)
      const startTimestamp = fromZonedTime(startDate, 'UTC').getTime()
      const endTimestamp = fromZonedTime(endOfDay(endDate), 'UTC').getTime()

      await fetchLoyaltyAnalytics({
        buildingId: buildingId,
        startDate: startTimestamp,
        endDate: endTimestamp,
      }).catch((error) => console.error(error.message))
      setLoadingAnalytics(false)
    }
  }

  useEffect(() => {
    if (buildingId) {
      fetchAnalytics()
    }
  }, [dateFilter, buildingId])

  useEffect(() => {
    setDateFilter(INITIAL_DATE_FILTER)
  }, [buildingId])

  if (!loyaltyAvailable && userRole !== 'Administrator') {
    return (
      <div className="rewards-container">
        <Upselling
          params={{ type: 'loyalty' }}
          containerStyle={{ height: 'calc(100vh - 320px)' }}
        />
      </div>
    )
  }

  return (
    <React.Fragment>
      {analytics && buildings && (
        <div className="rewards-container">
          <div
            className={`rewards-dashboard rewards-dashboard--loyalty-tab ${
              showAnalytics ? '' : 'hidden'
            }`}
          >
            <span className="rewards-dashboard__title">
              {t('rewards-dashboard:loyaltyTitle')}
              <button
                className="button button-black-outline"
                onClick={() => setShowAnalytics(!showAnalytics)}
              >
                <Icon icon={`${showAnalytics ? 'showEye' : 'hideEye'}`} />
                {showAnalytics
                  ? t('rewards-dashboard:hideButton')
                  : t('rewards-dashboard:showButton')}
              </button>
            </span>
            <div className={`rewards-dashboard__wrapper ${isLoadingAnalytics ? 'is-loading' : ''}`}>
              <AnalyticsItem
                setDateFilter={setDateFilter}
                dateFilter={dateFilter}
                analytics={analytics}
                mapping={getLoyaltyAnalyticsMapping(t)}
              />
            </div>
          </div>
        </div>
      )}
      <LoyaltyTable building={selectedBuilding} />
    </React.Fragment>
  )
}

RewardsLoyalty.propTypes = {
  userRole: PropTypes.string,
  buildings: PropTypes.array,
  analytics: PropTypes.object,
  fetchLoyaltyAnalytics: PropTypes.func,
  buildingData: PropTypes.object,
}

export default connect(
  (state) => ({
    userRole: selectUserRole(state),
    buildings: selectBuildingsForFilter(state),
    analytics: selectLoyaltyAnalytics(state),
    buildingData: selectActiveBuilding(state),
  }),
  {
    fetchLoyaltyAnalytics,
  },
)(RewardsLoyalty)
